import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface Etiket {
    etiketID: number;
    ad: string;
    renk: string;
    olusturmaTarihi: string;
}

const RENK_SECENEKLERI = [
    '#ef4444', '#f97316', '#eab308', '#22c55e',
    '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
    '#6b7280', '#1a2440',
];

const Etiketler: React.FC = () => {
    const { t } = useTranslation();
    const [etiketler, setEtiketler] = useState<Etiket[]>([]);
    const [yukleniyor, setYukleniyor] = useState(false);
    const [yeniAd, setYeniAd] = useState('');
    const [yeniRenk, setYeniRenk] = useState('#3b82f6');
    const [kaydediliyor, setKaydediliyor] = useState(false);

    const yukle = async () => {
        setYukleniyor(true);
        try {
            const res = await axiosInstance.get('/api/Etiketler');
            setEtiketler(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setYukleniyor(false);
        }
    };

    useEffect(() => { yukle(); }, []);

    const ekle = async () => {
        if (!yeniAd.trim()) { toast.warning(t('msg.uyari')); return; }
        setKaydediliyor(true);
        try {
            await axiosInstance.post('/api/Etiketler', { ad: yeniAd.trim(), renk: yeniRenk });
            toast.success('Etiket eklendi.');
            setYeniAd('');
            setYeniRenk('#3b82f6');
            yukle();
        } catch {
            toast.error('Etiket eklenemedi.');
        } finally {
            setKaydediliyor(false);
        }
    };

    const sil = async (id: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/Etiketler/${id}`);
            toast.success('Etiket silindi.');
            setEtiketler(prev => prev.filter(e => e.etiketID !== id));
        } catch {
            toast.error('Etiket silinemedi.');
        }
    };

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{t('ayarlar.etiket.baslik')}</h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Müşteri, lead ve diğer kayıtlara atanabilecek etiketleri yönetin.</p>
            </div>

            {/* Yeni Etiket Ekle */}
            <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '16px 20px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
            }}>
                <input
                    type="text"
                    placeholder={t('label.ara')}
                    value={yeniAd}
                    onChange={e => setYeniAd(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && ekle()}
                    style={{
                        flex: 1,
                        minWidth: 160,
                        padding: '8px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: 7,
                        fontSize: 13.5,
                        outline: 'none',
                    }}
                />

                {/* Renk seçici */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {RENK_SECENEKLERI.map(r => (
                        <button
                            key={r}
                            onClick={() => setYeniRenk(r)}
                            title={r}
                            style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                background: r,
                                border: yeniRenk === r ? '2.5px solid #0f172a' : '2px solid transparent',
                                cursor: 'pointer',
                                flexShrink: 0,
                                outline: 'none',
                            }}
                        />
                    ))}
                    {/* Custom renk input */}
                    <input
                        type="color"
                        value={yeniRenk}
                        onChange={e => setYeniRenk(e.target.value)}
                        title={t('label.tumunuSec')}
                        style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0 }}
                    />
                </div>

                <button
                    onClick={ekle}
                    disabled={kaydediliyor}
                    style={{
                        background: '#e67d21',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 7,
                        padding: '8px 18px',
                        fontSize: 13.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {kaydediliyor ? 'Ekleniyor...' : '+ Ekle'}
                </button>
            </div>

            {/* Etiket Listesi */}
            {yukleniyor ? (
                <p style={{ fontSize: 13, color: '#94a3b8' }}>{t('msg.yukleniyor')}</p>
            ) : etiketler.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 0',
                    color: '#94a3b8',
                    fontSize: 13.5,
                    border: '1.5px dashed #e2e8f0',
                    borderRadius: 10,
                }}>
                    Henüz etiket eklenmemiş.
                </div>
            ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {etiketler.map(e => (
                        <div
                            key={e.etiketID}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                background: '#fff',
                                border: '1.5px solid #e2e8f0',
                                borderRadius: 20,
                                padding: '6px 14px 6px 10px',
                                fontSize: 13.5,
                                fontWeight: 500,
                                color: '#1e293b',
                            }}
                        >
                            <span style={{
                                display: 'inline-block',
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                background: e.renk,
                                flexShrink: 0,
                            }} />
                            <span>{e.ad}</span>
                            <button
                                onClick={() => sil(e.etiketID)}
                                title={t('btn.sil')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#94a3b8',
                                    padding: '0 0 0 2px',
                                    fontSize: 14,
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Etiketler;
